import { useEffect, useRef, useState } from "react";
import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Skeleton,
  Modal,
  ModalContent,
  ModalBody,
} from "@heroui/react";
import { getNormalizedDate } from "@/utils";
import { Ticket } from "@/api/tickets/tickets.ts";
import { FaEllipsis, FaPencil, FaXmark } from "react-icons/fa6";
import { OfficeChip } from "../chips/officeChip";
import { RenterChip } from "../chips/renterChip";
import { S3_URL } from "@/api/s3";

export interface IAttachment {
  name: string;
  url: string;
  type: "image" | "file";
}

export interface IMessage {
  id: number;
  message: string;
  user: string;
  date: string;
  isMe: boolean;
  attachments?: string[];
}

export interface IChat {
  id: number;
  title: string;
  ticket: Ticket | null;
  messages: IMessage[];
  adminView: boolean;
  onEdit?: () => void;
}

export function Chat({ data }: { data: IChat }) {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [data.messages]);

  return (
    <div className="flex flex-col flex-grow gap-2 max-h-[calc(100vh-20%)] overflow-hidden">
      <div className="flex flex-col md:flex-row gap-2 md:gap-4 md:items-center p-2 px-4 rounded-xl bg-white dark:bg-zinc-900 border-2 border-zinc-300 border-opacity-50">
        <Skeleton className="rounded-lg" isLoaded={!!data.title}>
          <span className="text-lg font-semibold">«{data.title}»</span>
        </Skeleton>
        <Divider orientation="vertical" />
        {data.ticket?.creator_user_type == "user" ? (
          <>
            <RenterChip renterId={data.ticket?.renter_id || 0} />
            {data.ticket?.office_id && (
              <>
                <Divider orientation="vertical" />
                <OfficeChip officeId={data.ticket?.office_id || 0} />
              </>
            )}
          </>
        ) : (
          <>
            <span>Обращение к платформе</span>
          </>
        )}
        <div className="flex-grow" />
        <Dropdown>
          <DropdownTrigger>
            <Button variant="flat" size="sm">
              <FaEllipsis />
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Действия с тикетом">
            <DropdownItem
              key="edit"
              startContent={<FaPencil />}
              onClick={data.onEdit}
            >
              Редактировать
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
      <div className="flex flex-row gap-2 overflow-hidden">
        <div className="flex flex-col p-2 flex-grow max-h-[calc(100vh-30%)] overflow-auto">
          {data.messages.map((message) => (
            <Message
              key={message.id}
              adminView={data.adminView}
              message={message}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
}

export function Message({
  adminView,
  message,
}: {
  adminView: boolean;
  message: IMessage;
}) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleImageClick = (attachment: string) => {
    setSelectedImage(`${S3_URL}/${attachment}`);
  };

  const renderAttachments = () => {
    if (!message.attachments || message.attachments.length === 0) return null;

    const attachments = message.attachments;

    if (attachments.length === 1) {
      return (
        <div className="mt-2">
          <img
            src={`${S3_URL}/${attachments[0]}`}
            alt="Вложение"
            className="max-w-full max-h-64 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => handleImageClick(attachments[0])}
          />
        </div>
      );
    }

    if (attachments.length === 2) {
      return (
        <div className="mt-2 grid grid-cols-2 gap-1">
          {attachments.map((attachment, index) => (
            <img
              key={index}
              src={`${S3_URL}/${attachment}`}
              alt={`Вложение ${index + 1}`}
              className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => handleImageClick(attachment)}
            />
          ))}
        </div>
      );
    }

    if (attachments.length === 3) {
      return (
        <div className="mt-2 grid grid-cols-2 gap-1">
          <img
            src={`${S3_URL}/${attachments[0]}`}
            alt="Вложение 1"
            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => handleImageClick(attachments[0])}
          />
          <div className="grid grid-rows-2 gap-1">
            {attachments.slice(1).map((attachment, index) => (
              <img
                key={index + 1}
                src={`${S3_URL}/${attachment}`}
                alt={`Вложение ${index + 2}`}
                className="w-full h-15 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => handleImageClick(attachment)}
              />
            ))}
          </div>
        </div>
      );
    }

    // 4+ изображений
    return (
      <div className="mt-2 grid grid-cols-2 gap-1">
        <img
          src={`${S3_URL}/${attachments[0]}`}
          alt="Вложение 1"
          className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => handleImageClick(attachments[0])}
        />
        <div className="grid grid-rows-2 gap-1">
          {attachments.slice(1, 3).map((attachment, index) => (
            <img
              key={index + 1}
              src={`${S3_URL}/${attachment}`}
              alt={`Вложение ${index + 2}`}
              className="w-full h-15 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => handleImageClick(attachment)}
            />
          ))}
          {attachments.length > 4 && (
            <div className="relative">
              <img
                src={`${S3_URL}/${attachments[3]}`}
                alt="Вложение 4"
                className="w-full h-15 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => handleImageClick(attachments[3])}
              />
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  +{attachments.length - 4}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`flex flex-col w-full items-${!adminView && message.isMe ? "end" : "start"}`}
      >
        <div
          className={`flex flex-col items-${!adminView && message.isMe ? "end" : "start"} gap-2 p-2 max-w-lg`}
        >
          <span className="text-base font-medium">
            {adminView ? message.user : message.isMe ? "Вы" : message.user},{" "}
            {getNormalizedDate(message.date)}
          </span>
          <div
            className={`flex flex-col min-w-32 px-3 py-2 ${!adminView && message.isMe ? "bg-primary" : "bg-white dark:bg-zinc-800"} rounded-xl`}
          >
            {message.message && (
              <span
                className={`text-base break-all font-normal ${!adminView && message.isMe ? "text-white" : "text-foreground"}`}
              >
                {message.message}
              </span>
            )}
            {renderAttachments()}
          </div>
        </div>
      </div>

      <Modal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        size="3xl"
        classNames={{
          backdrop: "z-[1000]",
          wrapper: "z-[1000]",
        }}
      >
        <ModalContent>
          <ModalBody className="p-0">
            <div className="relative">
              <Button
                isIconOnly
                color="default"
                variant="flat"
                className="absolute top-2 right-2 z-10 bg-black bg-opacity-50 text-white"
                onClick={() => setSelectedImage(null)}
              >
                <FaXmark />
              </Button>
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="Просмотр изображения"
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              )}
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
